import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Platform, Pressable, Text, View, StyleSheet } from 'react-native';
import { useSelector } from 'react-redux';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { usePlannedMealTitle } from '../plannedMealTitle';
import { useHouseholdMembers } from '../../../../hooks/useHouseholdMembers';
import { Paprika } from '../../../../styles/theme';
import { CardMeta } from '../primitives/CardMeta';
import { CookedStamp } from '../../CookedStamp';
import { ApprovalThumbs } from '../../ApprovalThumbs';
import { CookRow } from '../../CookRow';
import { useVerdictPrompt } from '../../../household/VerdictPromptProvider';
import type { RootState } from '../../../../redux/store';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import type { DietaryRole } from '../../../../redux/mealPlanSlice';
import { makeDragStart } from './dragSource';
import { RemoteImage } from '../../../shared/RemoteImage';

interface Props {
  sk: number;
  recipe: RecipeIndexEntry;
  mealLabel: string;
  dietaryRole?: DietaryRole;
  flash?: boolean;
  dragOver?: boolean;
  /** This slot holds the next dish to cook — ringed so it reads at a glance (#579). */
  isNext?: boolean;
  isCooked?: boolean;
  /** Planned meal id — required to record an approval. */
  mealId?: string;
  onPhotoPress: () => void;
  onCardPress: () => void;
  onRemove?: () => void;
}

const isWeb = Platform.OS === 'web';
// Hover-revealed controls only make sense with a real pointer. On touch-only
// devices the button would be invisible yet tappable — don't render it there.
const canHover =
  isWeb && typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover)').matches;

export function SlotCard({ sk, recipe, mealLabel, flash, dragOver, isNext, isCooked, mealId, onPhotoPress, onCardPress, onRemove }: Props) {
  const { t } = useTranslation();
  const title = usePlannedMealTitle(recipe, 'desktop-slot');
  const { status: householdStatus } = useHouseholdMembers();
  const { openVerdict } = useVerdictPrompt();
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const cardRef = useRef<any>(null);

  // The stamp is a display of what the table said; tapping asks them.
  const handleRate = useCallback(() => {
    openVerdict({ recipeId: recipe.id, recipeTitle: recipe.title, surface: 'cooked_stamp' });
  }, [openVerdict, recipe.id, recipe.title]);

  // Setup drag + hover listeners on web. Hover is tracked with NATIVE
  // pointerenter/pointerleave on the card's DOM node instead of Pressable's
  // onHoverIn/Out: every RNW Pressable runs useHover with contain:true, so
  // child Pressables (the Remplacer chip, the ✕ itself) dispatch a
  // react-gui:hover:lock event that un-hovers the parent card the moment the
  // cursor reaches them — hiding the ✕ right as the user tries to click it.
  // Native enter/leave only fire at the card's outer boundary.
  useEffect(() => {
    if (!isWeb) return;

    const el = cardRef.current as HTMLElement | null;
    if (!el) return;

    const handleDragStart = makeDragStart(sk, setIsDragging);

    const handleDragEnd = () => {
      setIsDragging(false);
    };

    const handlePointerEnter = () => setIsHovered(true);
    const handlePointerLeave = () => setIsHovered(false);

    el.setAttribute('draggable', 'true');
    el.addEventListener('dragstart', handleDragStart);
    el.addEventListener('dragend', handleDragEnd);
    el.addEventListener('pointerenter', handlePointerEnter);
    el.addEventListener('pointerleave', handlePointerLeave);

    return () => {
      el.removeAttribute('draggable');
      el.removeEventListener('dragstart', handleDragStart);
      el.removeEventListener('dragend', handleDragEnd);
      el.removeEventListener('pointerenter', handlePointerEnter);
      el.removeEventListener('pointerleave', handlePointerLeave);
    };
  }, [sk]);

  return (
    <Pressable
      ref={cardRef}
      style={[
        styles.card,
        isNext && styles.cardNext,
        flash && styles.cardFlash,
        isDragging && styles.cardDragging,
        dragOver && styles.cardDragOver,
      ]}
      onPress={onPhotoPress}
    >
      {/* Photo area — whole card opens the recipe detail */}
      <View style={styles.photoWrap}>
        <RemoteImage
          uri={recipe.image}
          variant="thumb"
          style={styles.photo}
          resizeMode="cover"
          fallback={<View style={[styles.photo, styles.photoPlaceholder]} />}
        />
        <ApprovalThumbs mealId={mealId} variant="glass" />
        {/* Permanent replace chip — the card's explicit call to action */}
        <Pressable
          style={styles.replaceChip}
          testID="slot-replace"
          onPress={(e) => {
            e.stopPropagation?.();
            onCardPress();
          }}
        >
          <Feather name="refresh-cw" size={11} color={Paprika.textPrimary} />
          <Text style={styles.replaceChipText}>{householdStatus === 'paired' ? t('household.approval.propose') : t('recipeDetail.replaceHere')}</Text>
        </Pressable>
        {isCooked && <CookedStamp recipeId={recipe.id} size="desktop" onRate={handleRate} />}
      </View>

      {/* Content area */}
      <View style={styles.content}>
        <View style={styles.labelRow}>
          <Text style={styles.mealLabel}>{mealLabel}</Text>
          <CookRow mealId={mealId} variant="compact" isHovered={isHovered} />
        </View>
        <Text style={styles.title} numberOfLines={3} testID="planned-meal-title">{title}</Text>
        <CardMeta
          sourceUrl={recipe.sourceUrl}
          rating={recipe.sourceRating}
          minutes={recipe.readyInMinutes}
        />
      </View>

      {/* Hover-revealed remove button. Always mounted AND always interactive —
          visibility is opacity-only. Both conditional mounting and
          pointer-events toggling mutate hit-testing under the moving cursor,
          which re-targets pointer events at the button's boundary and makes
          the icon flicker. An invisible-but-clickable button is safe here:
          with a pointer, reaching it means hovering the card, which reveals
          it; hover-less (touch) devices don't render it at all. */}
      {canHover && onRemove && (
        <Pressable
          style={[styles.removeBtn, !isHovered && styles.removeBtnHidden]}
          onPress={(e) => {
            e.stopPropagation?.();
            onRemove();
          }}
        >
          <Feather name="x" size={14} color={Paprika.textPrimary} />
        </Pressable>
      )}

    </Pressable>
  );
}

const styles = StyleSheet.create({
  // Fills its row: the open row of the grid is as tall as the screen allows
  // (rowFocus.ts), and the photo takes what the text below it does not need.
  card: {
    flex: 1,
    minHeight: 192,
    borderRadius: 10,
    backgroundColor: Paprika.surfaceLowest,
    overflow: 'hidden',
    shadowColor: 'rgba(82, 68, 60, 0.08)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 2,
  },
  cardFlash: {
    borderWidth: 2,
    borderColor: Paprika.primary,
  },
  // Ring rather than a fill: the card still has to read as one of the seven,
  // just the one you're cooking next.
  cardNext: {
    borderWidth: 2,
    borderColor: Paprika.primary,
    shadowColor: 'rgba(134, 78, 44, 0.20)',
    shadowRadius: 14,
  },
  cardDragging: {
    opacity: 0.4,
  } as any,
  cardDragOver: {
    borderWidth: 1.5,
    borderColor: Paprika.primary,
  },
  photoWrap: {
    flex: 1,
    minHeight: 88,
    position: 'relative',
    backgroundColor: Paprika.surfaceLow,
  },
  photo: {
    width: '100%' as any,
    height: '100%' as any,
  },
  photoPlaceholder: {
    backgroundColor: Paprika.bgSunk,
  },
  content: {
    padding: 10,
    gap: 6,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
  },
  mealLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 9,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
  },
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 15,
    lineHeight: 19,
    color: Paprika.textPrimary,
  },
  removeBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: Paprika.surfaceLowest,
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 2px 8px rgba(82, 68, 60, 0.12)',
    // Smooth the reveal so the hover transition never reads as a blink (web CSS).
    transitionProperty: 'opacity',
    transitionDuration: '150ms',
  } as any,
  removeBtnHidden: {
    opacity: 0,
  },
  replaceChip: {
    position: 'absolute',
    bottom: 6,
    right: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.9)',
    boxShadow: '0 2px 8px rgba(82, 68, 60, 0.12)',
  } as any,
  replaceChipText: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    color: Paprika.textPrimary,
  },
});
